import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminSettings = () => {
  const { user } = useAuth();
  
  // Email integration state
  const [emailSettings, setEmailSettings] = useState({
    provider: 'sendgrid',
    api_key_set: false,
    sender_email: '',
    sender_name: '',
    is_enabled: false,
    last_tested_at: null,
    last_test_success: null,
    last_error: null
  });
  
  // Form state
  const [formData, setFormData] = useState({
    sender_email: '',
    sender_name: '',
    api_key: '',
    is_enabled: false
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  
  // Audit logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  
  // Default Logo state
  const [defaultLogoUrl, setDefaultLogoUrl] = useState(null);
  const [defaultLogoFile, setDefaultLogoFile] = useState(null);
  const [defaultLogoPreview, setDefaultLogoPreview] = useState(null);
  const [uploadingDefaultLogo, setUploadingDefaultLogo] = useState(false);
  const [defaultLogoUpdatedAt, setDefaultLogoUpdatedAt] = useState(null);
  const [defaultLogoUpdatedBy, setDefaultLogoUpdatedBy] = useState(null);

  const fetchEmailSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/integrations/email?user_id=${user.id}`);
      setEmailSettings(response.data);
      setFormData({
        sender_email: response.data.sender_email || '',
        sender_name: response.data.sender_name || '',
        api_key: '',
        is_enabled: response.data.is_enabled || false
      });
    } catch (err) {
      console.error('Error fetching email settings:', err);
      setError('Failed to load email settings');
    }
    setLoading(false);
  }, [user.id]);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/integrations/audit-log?user_id=${user.id}`);
      setAuditLogs(response.data.logs || []);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    }
  }, [user.id]);

  const fetchDefaultLogo = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/default-logo`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.logo_url) {
        setDefaultLogoUrl(response.data.logo_url);
        setDefaultLogoPreview(response.data.logo_url);
        setDefaultLogoUpdatedAt(response.data.updated_at);
        setDefaultLogoUpdatedBy(response.data.updated_by);
      }
    } catch (err) {
      console.error('Error fetching default logo:', err);
    }
  }, []);

  useEffect(() => {
    fetchEmailSettings();
    fetchAuditLogs();
    fetchDefaultLogo();
  }, [fetchEmailSettings, fetchAuditLogs, fetchDefaultLogo]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Default Logo handlers
  const handleDefaultLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a valid image file (PNG, JPG, GIF, WebP, or SVG)');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file must be less than 5MB');
        return;
      }
      setDefaultLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setDefaultLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleUploadDefaultLogo = async () => {
    if (!defaultLogoFile) return;
    
    setUploadingDefaultLogo(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      const contentType = defaultLogoFile.type || 'image/png';
      
      // Step 1: Get presigned URL
      const urlResponse = await axios.post(
        `${API}/admin/default-logo/upload?content_type=${encodeURIComponent(contentType)}`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      const { upload_url, logo_url, file_key } = urlResponse.data;
      
      if (!upload_url) {
        throw new Error('Failed to get upload URL from server');
      }
      
      // Step 2: Upload to S3
      await axios.put(upload_url, defaultLogoFile, {
        headers: { 'Content-Type': contentType }
      });
      
      // Step 3: Save the logo URL to database
      await axios.put(
        `${API}/admin/default-logo?logo_url=${encodeURIComponent(logo_url)}&file_key=${encodeURIComponent(file_key || '')}`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setDefaultLogoUrl(logo_url);
      setDefaultLogoFile(null);
      setSuccess('Default program logo uploaded successfully!');
      fetchDefaultLogo();
      fetchAuditLogs();
    } catch (err) {
      console.error('Error uploading default logo:', err);
      if (err.response?.status === 400) {
        setError(err.response?.data?.detail || 'Logo upload failed - S3 storage may not be configured');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to upload the default logo');
      } else {
        setError(err.message || 'Failed to upload default logo. Please try again.');
      }
    } finally {
      setUploadingDefaultLogo(false);
    }
  };

  const handleRemoveDefaultLogo = async () => {
    if (!window.confirm('Are you sure you want to remove the default program logo?')) return;
    
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/default-logo`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setDefaultLogoUrl(null);
      setDefaultLogoPreview(null);
      setDefaultLogoFile(null);
      setDefaultLogoUpdatedAt(null);
      setDefaultLogoUpdatedBy(null);
      setSuccess('Default logo removed successfully');
      fetchAuditLogs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove default logo');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const updateData = {
        sender_email: formData.sender_email,
        sender_name: formData.sender_name,
        is_enabled: formData.is_enabled
      };
      
      // Only include API key if it was changed
      if (formData.api_key) {
        updateData.api_key = formData.api_key;
      }

      await axios.put(`${API}/admin/integrations/email?user_id=${user.id}`, updateData);
      
      setSuccess('Settings saved successfully');
      setFormData(prev => ({ ...prev, api_key: '' }));
      setShowApiKeyInput(false);
      fetchEmailSettings();
      fetchAuditLogs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings');
    }
    setSaving(false);
  };

  const handleTestEmail = async () => {
    setTesting(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(
        `${API}/admin/integrations/email/test?user_id=${user.id}`,
        { test_email_address: testEmailAddress || null }
      );
      
      if (response.data.success) {
        setSuccess(response.data.message);
      } else {
        setError(response.data.error || 'Test failed');
      }
      fetchEmailSettings();
      fetchAuditLogs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send test email');
    }
    setTesting(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Breadcrumb */}
      <nav className="mb-4">
        <ol className="flex items-center space-x-2 text-sm">
          <li>
            <span className="text-gray-500">Settings</span>
          </li>
          <li>
            <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </li>
          <li>
            <span className="text-red-600 font-medium">Integration Settings</span>
          </li>
        </ol>
      </nav>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Integration Settings</h1>
        <p className="mt-2 text-gray-600">Configure external service integrations for the platform</p>
      </div>

      {/* Not Configured Warning */}
      {!emailSettings.api_key_set && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-amber-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-amber-800 font-medium">Email Integration Not Configured</h3>
              <p className="text-amber-700 text-sm mt-1">
                Email notifications are currently disabled. To enable transactional emails (signup confirmations, activation notices), 
                please configure your SendGrid API key below.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Alerts */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-700">{error}</span>
            <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-green-700">{success}</span>
            <button onClick={() => setSuccess('')} className="ml-auto text-green-500 hover:text-green-700">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Integrations Section */}
      <div className="bg-white rounded-lg shadow mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <svg className="h-6 w-6 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
            </svg>
            Integrations
          </h2>
        </div>

        {/* Email Service Card */}
        <div className="p-6">
          <div className="border border-gray-200 rounded-lg p-6">
            {/* Card Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="h-12 w-12 bg-sky-100 rounded-lg flex items-center justify-center mr-4">
                  <svg className="h-6 w-6 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Email Service (SendGrid)</h3>
                  <p className="text-sm text-gray-500">Configure transactional email delivery</p>
                </div>
              </div>
              <div className="flex items-center">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  emailSettings.is_enabled && emailSettings.api_key_set
                    ? 'bg-green-100 text-green-800'
                    : emailSettings.api_key_set
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {emailSettings.is_enabled && emailSettings.api_key_set
                    ? '● Enabled'
                    : emailSettings.api_key_set
                    ? '● Configured (Disabled)'
                    : '○ Not Configured'}
                </span>
              </div>
            </div>

            {/* Status Info */}
            {emailSettings.last_tested_at && (
              <div className={`mb-6 p-4 rounded-lg ${
                emailSettings.last_test_success 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center">
                  <svg className={`h-5 w-5 mr-2 ${
                    emailSettings.last_test_success ? 'text-green-500' : 'text-red-500'
                  }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {emailSettings.last_test_success ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    )}
                  </svg>
                  <span className={emailSettings.last_test_success ? 'text-green-700' : 'text-red-700'}>
                    Last tested: {formatDate(emailSettings.last_tested_at)} - 
                    {emailSettings.last_test_success ? ' Success' : ` Failed: ${emailSettings.last_error}`}
                  </span>
                </div>
              </div>
            )}

            {/* Form */}
            <div className="space-y-6">
              {/* Provider (Read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Provider
                </label>
                <input
                  type="text"
                  value="SendGrid"
                  disabled
                  className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-600"
                />
              </div>

              {/* API Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SendGrid API Key
                </label>
                {emailSettings.api_key_set && !showApiKeyInput ? (
                  <div className="flex items-center">
                    <div className="flex-1 px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-600">
                      ••••••••••••••••••••••••••••••••
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowApiKeyInput(true)}
                      className="ml-3 px-4 py-2 text-sm font-medium text-sky-600 hover:text-sky-700 border border-sky-300 rounded-lg hover:bg-sky-50"
                    >
                      Replace Key
                    </button>
                  </div>
                ) : (
                  <div>
                    <input
                      type="password"
                      name="api_key"
                      value={formData.api_key}
                      onChange={handleInputChange}
                      placeholder={emailSettings.api_key_set ? "Enter new API key to replace" : "Enter SendGrid API key"}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    />
                    {showApiKeyInput && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowApiKeyInput(false);
                          setFormData(prev => ({ ...prev, api_key: '' }));
                        }}
                        className="mt-2 text-sm text-gray-500 hover:text-gray-700"
                      >
                        Cancel replacement
                      </button>
                    )}
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  🔒 API keys are stored securely and never displayed after saving.
                </p>
              </div>

              {/* Sender Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Verified Sender Email *
                </label>
                <input
                  type="email"
                  name="sender_email"
                  value={formData.sender_email}
                  onChange={handleInputChange}
                  placeholder="support@exlva.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Must be a verified sender in your SendGrid account.
                </p>
              </div>

              {/* Sender Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sender Name
                </label>
                <input
                  type="text"
                  name="sender_name"
                  value={formData.sender_name}
                  onChange={handleInputChange}
                  placeholder="Exlva Support Team"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                />
              </div>

              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Enable Email Notifications</h4>
                  <p className="text-sm text-gray-500">
                    When enabled, the system will send transactional emails for signup, activation, and deactivation.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_enabled"
                    checked={formData.is_enabled}
                    onChange={handleInputChange}
                    className="sr-only peer"
                    disabled={!emailSettings.api_key_set && !formData.api_key}
                  />
                  <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-600 ${
                    !emailSettings.api_key_set && !formData.api_key ? 'opacity-50 cursor-not-allowed' : ''
                  }`}></div>
                </label>
              </div>

              {/* Save Button */}
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Save Settings
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Divider */}
            <hr className="my-6 border-gray-200" />

            {/* Test Email Section */}
            <div>
              <h4 className="font-medium text-gray-900 mb-4">Test Email Configuration</h4>
              <div className="flex items-end space-x-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Test Email Address (optional)
                  </label>
                  <input
                    type="email"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    placeholder={user.email}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Leave empty to send to your email ({user.email})
                  </p>
                </div>
                <button
                  onClick={handleTestEmail}
                  disabled={testing || !emailSettings.api_key_set}
                  className="px-6 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center whitespace-nowrap"
                >
                  {testing ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Send Test Email
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Help Text */}
            <div className="mt-6 p-4 bg-sky-50 border border-sky-200 rounded-lg">
              <h5 className="font-medium text-sky-800 mb-2 flex items-center">
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Security Notes
              </h5>
              <ul className="text-sm text-sky-700 space-y-1">
                <li>• API keys are stored securely on the server and never exposed in responses.</li>
                <li>• You can rotate keys anytime using the "Replace Key" option.</li>
                <li>• Test email validates connectivity before enabling in production.</li>
                <li>• All integration changes are logged for audit purposes.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Default Program Logo Section */}
      <div className="bg-white rounded-lg shadow mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <svg className="h-6 w-6 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Default Program Logo
          </h2>
        </div>

        <div className="p-6">
          <p className="text-gray-600 mb-6">
            Set a default logo for the DARe program. This logo will be displayed for users and institutions 
            that have not uploaded their own logo. User logos will override this default.
          </p>

          <div className="flex items-start gap-8">
            {/* Logo Preview */}
            <div className="flex-shrink-0">
              <div className="w-40 h-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                {defaultLogoPreview ? (
                  <img 
                    src={defaultLogoPreview} 
                    alt="Default logo preview" 
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="text-center text-gray-400">
                    <svg className="h-12 w-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-xs">No logo set</span>
                  </div>
                )}
              </div>
              {defaultLogoUpdatedAt && (
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Last updated: {formatDate(defaultLogoUpdatedAt)}
                  {defaultLogoUpdatedBy && <br />}
                  {defaultLogoUpdatedBy && `by ${defaultLogoUpdatedBy}`}
                </p>
              )}
            </div>

            {/* Upload Controls */}
            <div className="flex-1 space-y-4">
              <div>
                <input
                  type="file"
                  id="default-logo-upload"
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml"
                  onChange={handleDefaultLogoChange}
                  className="hidden"
                />
                <label htmlFor="default-logo-upload">
                  <span className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                    <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Choose Logo File
                  </span>
                </label>
                <p className="mt-2 text-sm text-gray-500">
                  PNG, JPG, GIF, WebP, or SVG. Maximum 5MB. Recommended: 200x200px or larger.
                </p>
              </div>

              {defaultLogoFile && (
                <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg">
                  <p className="text-sm text-sky-800">
                    <strong>Selected:</strong> {defaultLogoFile.name} ({Math.round(defaultLogoFile.size / 1024)}KB)
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  onClick={handleUploadDefaultLogo}
                  disabled={!defaultLogoFile || uploadingDefaultLogo}
                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {uploadingDefaultLogo ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Upload Logo
                    </>
                  )}
                </button>

                {defaultLogoUrl && (
                  <button
                    onClick={handleRemoveDefaultLogo}
                    className="px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    Remove Logo
                  </button>
                )}
              </div>

              {/* Info Box */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h5 className="font-medium text-amber-800 mb-2 flex items-center">
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  How it works
                </h5>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• This logo appears for users who haven't uploaded their own logo.</li>
                  <li>• User-uploaded logos always take priority over this default.</li>
                  <li>• The logo is stored in secure cloud storage (S3).</li>
                  <li>• Changes take effect immediately for all applicable users.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Audit Log Section */}
      <div className="bg-white rounded-lg shadow">
        <div 
          className="px-6 py-4 border-b border-gray-200 flex items-center justify-between cursor-pointer"
          onClick={() => setShowAuditLogs(!showAuditLogs)}
        >
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <svg className="h-6 w-6 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Integration Audit Log
          </h2>
          <svg 
            className={`h-5 w-5 text-gray-500 transform transition-transform ${showAuditLogs ? 'rotate-180' : ''}`} 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {showAuditLogs && (
          <div className="p-6">
            {auditLogs.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No audit logs yet.</p>
            ) : (
              <div className="space-y-3">
                {auditLogs.map((log, index) => (
                  <div key={log.id || index} className="flex items-start p-3 bg-gray-50 rounded-lg">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${
                      log.action === 'tested' ? 'bg-purple-100' :
                      log.action === 'enabled' ? 'bg-green-100' :
                      log.action === 'disabled' ? 'bg-red-100' :
                      'bg-sky-100'
                    }`}>
                      <svg className={`h-4 w-4 ${
                        log.action === 'tested' ? 'text-purple-600' :
                        log.action === 'enabled' ? 'text-green-600' :
                        log.action === 'disabled' ? 'text-red-600' :
                        'text-sky-600'
                      }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {log.action === 'tested' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        )}
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {log.action.charAt(0).toUpperCase() + log.action.slice(1)}
                      </p>
                      {log.details && (
                        <p className="text-sm text-gray-600">{log.details}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {log.user_email} • {formatDate(log.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminSettings;
